import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const badgeVariants = cva(
  'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-4xl border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground [a]:hover:bg-primary/80',
        secondary: 'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
        destructive:
          'bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20',
        outline: 'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
        ghost: 'text-(--badge-solid) hover:bg-muted dark:hover:bg-muted/50',
        link: 'text-primary underline-offset-4 hover:underline',
        // VOLL: soft/solid representam o quarteto semântico e consomem as
        // mesmas custom properties do Alert — um status, um valor.
        soft: 'bg-(--badge-soft) text-(--badge-strong)',
        solid: 'bg-(--badge-solid) text-white',
      },
      color: {
        neutral:
          '[--badge-soft:var(--muted)] [--badge-solid:var(--primary)] [--badge-strong:var(--card-foreground)]',
        info: '[--badge-soft:var(--info-soft)] [--badge-solid:var(--info)] [--badge-strong:var(--info-strong)]',
        success:
          '[--badge-soft:var(--success-soft)] [--badge-solid:var(--success)] [--badge-strong:var(--success-strong)]',
        warning:
          '[--badge-soft:var(--warning-soft)] [--badge-solid:var(--warning)] [--badge-strong:var(--warning-strong)]',
        danger:
          '[--badge-soft:var(--danger-soft)] [--badge-solid:var(--destructive)] [--badge-strong:var(--danger-strong)]',
      },
    },
    defaultVariants: {
      variant: 'default',
      color: 'neutral',
    },
  },
);

function Badge({
  className,
  variant = 'default',
  color,
  render,
  ...props
}: Omit<useRender.ComponentProps<'span'>, 'color'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant, color }), className),
      },
      props,
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  });
}

export { Badge, badgeVariants };
